import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Label } from "@bcc/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { cn } from "@bcc/lib/utils";
import type { AdminAttributeChange, AdminAttributeField } from "@shared/types/admin-product-update";
import { useMutation } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { Loader2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type EditableCatalogueRow, useAdminProductEdit } from "./admin-product-edit-context";
import {
  ATTRIBUTE_LABEL_KEYS,
  EDIT_CONTROL_CURSOR,
  ATTRIBUTE_OPTIONS,
  EDITOR_OWNER_ATTRIBUTE,
  buildAttributeChange,
  isDraftChanged,
  setAdminAttributeEditTestId,
  shouldCloseOnOutsideInteraction,
  toAttributeDraft,
} from "./admin-product-edit.utils";
import { useAdminUpdateToast } from "./admin-update-toast";

/** Display text for an attribute value: intensity zones are translated, the rest are Salesforce picklist values. */
export const attributeValueLabel = (t: TFunction, field: AdminAttributeField, value: string): string =>
  field === "intensityZone" ? String(t(`admin.productCatalogue.intensityZones.${value}`)) : value;

const formatValue = (t: TFunction, field: AdminAttributeField, value: AdminAttributeChange["oldValue"]): string => {
  if (value === null || (Array.isArray(value) && value.length === 0)) return "–";
  if (Array.isArray(value)) return value.join(", ");
  return attributeValueLabel(t, field, String(value));
};

interface AttributeEditorProps {
  readonly field: AdminAttributeField;
  readonly draft: string[];
  readonly onChange: (draft: string[]) => void;
  readonly editorId: string;
}

function AttributeEditor({ field, draft, onChange, editorId }: AttributeEditorProps) {
  const { t } = useTranslation();
  const options = ATTRIBUTE_OPTIONS[field];
  if (field === "badges") {
    const toggle = (badge: string, checked: boolean) => onChange(checked ? [...draft, badge] : draft.filter((value) => value !== badge));
    return (
      <div className="max-h-64 space-y-2 overflow-y-auto" data-testid={setAdminAttributeEditTestId("badges")}>
        {options.map((badge) => (
          <Label key={badge} className="flex cursor-pointer items-center gap-2 text-sm font-normal">
            <Checkbox
              className={EDIT_CONTROL_CURSOR}
              checked={draft.includes(badge)}
              onCheckedChange={(checked) => toggle(badge, checked === true)}
              testId={setAdminAttributeEditTestId(`badge-${badge}`)}
            />
            {badge}
          </Label>
        ))}
      </div>
    );
  }
  return (
    <Select value={draft[0] ?? ""} onValueChange={(value) => onChange([value])}>
      <SelectTrigger
        className={cn("w-full", EDIT_CONTROL_CURSOR)}
        aria-label={t(ATTRIBUTE_LABEL_KEYS[field])}
        testId={setAdminAttributeEditTestId(`${field}-trigger`)}
      >
        <SelectValue placeholder={t("admin.productCatalogue.edit.selectValue")} />
      </SelectTrigger>
      <SelectContent {...{ [EDITOR_OWNER_ATTRIBUTE]: editorId }}>
        {options.map((value) => (
          <SelectItem key={value} className="cursor-pointer" value={value} testId={setAdminAttributeEditTestId(`${field}-${value}`)}>
            {attributeValueLabel(t, field, value)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ConfirmAttributeDialogProps {
  readonly row: EditableCatalogueRow;
  readonly change: AdminAttributeChange;
  readonly onClose: () => void;
}

/**
 * Lists every SKU that gets its own approval request before anything is sent. Owns the submission, so nothing about
 * it is mounted until a manager reviews a change.
 */
function ConfirmAttributeDialog({ row, change, onClose }: ConfirmAttributeDialogProps) {
  const { t } = useTranslation();
  const edit = useAdminProductEdit();
  const { notifySuccess, notifyError } = useAdminUpdateToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!edit) throw new Error("Admin product editing is not available");
      return await edit.submitAttributeUpdate(row, change);
    },
    onSuccess: (response) => {
      notifySuccess(response);
      onClose();
    },
    // The toast explains the failure; the field stays editable for another try.
    onError: (error) => {
      notifyError(error);
      onClose();
    },
  });
  // Only the SKUs without a request yet: after a partial success the retry covers just the failed ones.
  const skus = edit?.getPendingSkus(row.productId, change.field) ?? [];
  const field = t(ATTRIBUTE_LABEL_KEYS[change.field]);
  const isSubmitting = mutation.isPending;
  return (
    <Dialog open onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent testId={setAdminAttributeEditTestId("confirm")} closeButtonTestId={setAdminAttributeEditTestId("confirm-close")}>
        <DialogHeader>
          <DialogTitle>{t("admin.productCatalogue.edit.confirmTitle", { field })}</DialogTitle>
          <DialogDescription>{t("admin.productCatalogue.edit.confirmDescription", { count: skus.length })}</DialogDescription>
        </DialogHeader>
        <p className="text-sm" data-testid={setAdminAttributeEditTestId("confirm-change")}>
          {formatValue(t, change.field, change.oldValue)} → {formatValue(t, change.field, change.newValue)}
        </p>
        <ul
          className="max-h-60 divide-y overflow-y-auto rounded-md border text-sm"
          data-testid={setAdminAttributeEditTestId("confirm-skus")}
        >
          {skus.map((sku) => (
            <li key={sku.id} className="flex justify-between gap-4 px-3 py-2">
              <span className="font-mono">{sku.itemNumber}</span>
              <span className="text-muted-foreground">{sku.bagSize ?? "–"}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button
            variant="outline"
            className={EDIT_CONTROL_CURSOR}
            onClick={onClose}
            disabled={isSubmitting}
            testId={setAdminAttributeEditTestId("confirm-cancel")}
          >
            {t("common.cancel")}
          </Button>
          <Button
            className={EDIT_CONTROL_CURSOR}
            onClick={() => mutation.mutate()}
            disabled={isSubmitting}
            testId={setAdminAttributeEditTestId("confirm-submit")}
          >
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("admin.productCatalogue.edit.submitForApproval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AttributePanelProps {
  readonly field: AdminAttributeField;
  readonly row: EditableCatalogueRow;
  readonly onCancel: () => void;
  readonly onReview: (change: AdminAttributeChange) => void;
  /** Told whether the picked value differs from the current one, so a click outside can keep or close the editor. */
  readonly onChangesChange: (hasChanges: boolean) => void;
  readonly editorId: string;
}

interface RetryNoticeProps {
  readonly change: AdminAttributeChange;
  readonly skuCount: number;
}

// After a partial success the value is pinned: the remaining SKUs must get the same change their siblings did.
function RetryNotice({ change, skuCount }: RetryNoticeProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-sm" data-testid={setAdminAttributeEditTestId("retry")}>
      <p className="text-muted-foreground">{t("admin.productCatalogue.edit.retryDescription", { count: skuCount })}</p>
      <p>
        {formatValue(t, change.field, change.oldValue)} → {formatValue(t, change.field, change.newValue)}
      </p>
    </div>
  );
}

// Mounted only while the popover is open, so it starts from the row's current value each time.
function AttributePanel({ field, row, onCancel, onReview, onChangesChange, editorId }: AttributePanelProps) {
  const { t } = useTranslation();
  const edit = useAdminProductEdit();
  const [initialDraft] = useState(() => toAttributeDraft(row, field));
  const [draft, setDraft] = useState(initialDraft);
  const retry = edit?.getRetryChange(row.productId, field);
  const change = retry ?? buildAttributeChange(row, field, draft);
  const hasChanges = isDraftChanged(initialDraft, draft);
  useEffect(() => onChangesChange(hasChanges), [hasChanges, onChangesChange]);
  return (
    <>
      <h4 className="text-sm font-medium">{t("admin.productCatalogue.edit.title", { field: t(ATTRIBUTE_LABEL_KEYS[field]) })}</h4>
      {retry ? (
        <RetryNotice change={retry} skuCount={edit?.getPendingSkus(row.productId, field).length ?? 0} />
      ) : (
        <AttributeEditor field={field} draft={draft} onChange={setDraft} editorId={editorId} />
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn("flex-1", EDIT_CONTROL_CURSOR)}
          onClick={onCancel}
          testId={setAdminAttributeEditTestId("cancel")}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          className={cn("flex-1", EDIT_CONTROL_CURSOR)}
          disabled={!change}
          onClick={() => change && onReview(change)}
          testId={setAdminAttributeEditTestId("review")}
        >
          {t("admin.productCatalogue.edit.review")}
        </Button>
      </div>
    </>
  );
}

interface AdminAttributePopoverProps {
  readonly field: AdminAttributeField;
  readonly row: EditableCatalogueRow;
  readonly children: ReactNode;
}

/** Edits one product-level attribute; confirming raises an approval request for every SKU of the product. */
export function AdminAttributePopover({ field, row, children }: AdminAttributePopoverProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<AdminAttributeChange>();
  const editorId = useId();
  const hasChangesRef = useRef(false);
  const trackChanges = useCallback((hasChanges: boolean) => {
    hasChangesRef.current = hasChanges;
  }, []);

  const review = (change: AdminAttributeChange) => {
    setOpen(false);
    setPending(change);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          className="w-72 space-y-4"
          align="start"
          testId={setAdminAttributeEditTestId(`${field}-popover`)}
          // Untouched, it closes like any popover; once a value is picked it stays open until Cancel or Review.
          onInteractOutside={(event) => {
            if (!shouldCloseOnOutsideInteraction(event.target, hasChangesRef.current, editorId)) event.preventDefault();
          }}
        >
          <AttributePanel
            field={field}
            row={row}
            onCancel={() => setOpen(false)}
            onReview={review}
            onChangesChange={trackChanges}
            editorId={editorId}
          />
        </PopoverContent>
      </Popover>
      {pending ? <ConfirmAttributeDialog row={row} change={pending} onClose={() => setPending(undefined)} /> : null}
    </>
  );
}
